import React, { useState } from 'react';
import { Player, Expense, Payment } from '../types';

interface FoodMiscFeesProps {
  players: Player[];
  expenses: Expense[];
  payments: Payment[];
  onUpdateExpenses: (expenses: Expense[]) => void;
  onUpdatePayments: (payments: Payment[]) => void;
}

export const FoodMiscFees: React.FC<FoodMiscFeesProps> = ({ players, expenses, payments, onUpdateExpenses, onUpdatePayments }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());
  const [isPaymentSummaryOpen, setIsPaymentSummaryOpen] = useState(false);

  // Form state for adding new expenses
  const [newExpenseType, setNewExpenseType] = useState('');
  const [newExpenseDetails, setNewExpenseDetails] = useState('');
  const [newExpenseAmount, setNewExpenseAmount] = useState('');
  const [newExpensePaidBy, setNewExpensePaidBy] = useState('');

  // Payment editing state
  const [editingPaymentColumn, setEditingPaymentColumn] = useState<string | null>(null);
  const [editPaymentAmount, setEditPaymentAmount] = useState('');

  // Expense editing state
  const [editingExpense, setEditingExpense] = useState<string | null>(null);
  const [editExpenseType, setEditExpenseType] = useState('');
  const [editExpenseDetails, setEditExpenseDetails] = useState('');
  const [editExpenseAmount, setEditExpenseAmount] = useState('');

  // Filter food and misc expenses
  const foodMiscExpenses = expenses.filter(e => e.categoryId === 'food');
  const totalExpenses = foodMiscExpenses.reduce((sum, e) => sum + e.amount, 0);

  // Group expenses by player who paid
  const expensesByPlayer = players.reduce((acc, player) => {
    const playerExpenses = foodMiscExpenses.filter(e => e.paidBy === player.id);
    const totalPaid = playerExpenses.reduce((sum, e) => sum + e.amount, 0);

    if (totalPaid > 0) {
      acc[player.id] = {
        player,
        expenses: playerExpenses,
        totalPaid
      };
    }

    return acc;
  }, {} as Record<string, { player: Player; expenses: Expense[]; totalPaid: number }>);

  // Toggle player accordion
  const togglePlayerAccordion = (playerId: string) => {
    const newExpanded = new Set(expandedPlayers);
    if (newExpanded.has(playerId)) {
      newExpanded.delete(playerId);
    } else {
      newExpanded.add(playerId);
    }
    setExpandedPlayers(newExpanded);
  };

  // Add new expense
  const addExpense = () => {
    if (newExpenseType.trim() && newExpenseDetails.trim() && newExpenseAmount && newExpensePaidBy) {
      const amount = parseFloat(newExpenseAmount);
      if (amount > 0) {
        const newExpense: Expense = {
          id: Date.now().toString(),
          description: newExpenseType.trim(),
          amount,
          paidBy: newExpensePaidBy,
          categoryId: 'food',
          details: newExpenseDetails.trim(),
          createdAt: new Date()
        };

        onUpdateExpenses([...expenses, newExpense]);

        // Reset form
        setNewExpenseType('');
        setNewExpenseDetails('');
        setNewExpenseAmount('');
        setNewExpensePaidBy('');
      }
    }
  };


  // Calculate per head share for food/misc
  const activePlayerCount = players.filter(p => p.isActive).length;
  const perHeadShare = activePlayerCount > 0 ? Math.round(totalExpenses / activePlayerCount) : 0;

  // Get player payment info for food category
  const getPlayerPaymentInfo = (playerId: string) => {
    const foodPayments = payments.filter(p => p.playerId === playerId && p.categoryId === 'food' && p.isPaid);
    const totalPayments = foodPayments.reduce((sum, p) => sum + p.amount, 0);

    const playerExpenses = foodMiscExpenses.filter(e => e.paidBy === playerId);
    const totalSpent = playerExpenses.reduce((sum, e) => sum + e.amount, 0);

    // Balance calculation: What they owe (per head share) minus what they've already contributed (expenses + payments)
    // Positive balance = owes money, Negative balance = owed money
    const balance = perHeadShare - (totalSpent + totalPayments);

    return {
      payments: totalPayments,
      spent: totalSpent,
      balance: balance,
      status: balance === 0 ? 'Even' :
              balance > 0 ? `Owes ₹${balance.toLocaleString()}` :
              `Owed ₹${Math.abs(balance).toLocaleString()}`
    };
  };

  // Update player payment for food category
  const updatePlayerPayment = (playerId: string, amount: number) => {
    if (amount < 0) return;

    // Remove existing food payments for this player
    const filteredPayments = payments.filter(p =>
      !(p.playerId === playerId && p.categoryId === 'food')
    );

    // Add new payment only if amount > 0
    if (amount > 0) {
      const newPayment: Payment = {
        id: Date.now().toString(),
        playerId,
        categoryId: 'food',
        amount,
        isPaid: true,
        paidAt: new Date(),
        isAdvance: false,
        isPartialPayment: false
      };

      onUpdatePayments([...filteredPayments, newPayment]);
    } else {
      // If amount is 0, just remove existing payments
      onUpdatePayments(filteredPayments);
    }
  };

  // Start editing payment
  const startEditPayment = (playerId: string) => {
    const playerInfo = getPlayerPaymentInfo(playerId);
    setEditingPaymentColumn(playerId);
    setEditPaymentAmount(playerInfo.payments.toString());
  };

  // Save payment edit
  const savePaymentEdit = () => {
    if (editingPaymentColumn && editPaymentAmount !== '') {
      const amount = parseFloat(editPaymentAmount) || 0;
      updatePlayerPayment(editingPaymentColumn, amount);
      setEditingPaymentColumn(null);
      setEditPaymentAmount('');
    }
  };

  // Cancel payment edit
  const cancelPaymentEdit = () => {
    setEditingPaymentColumn(null);
    setEditPaymentAmount('');
  };

  // Start editing expense
  const startEditExpense = (expense: Expense) => {
    setEditingExpense(expense.id);
    setEditExpenseType(expense.description);
    setEditExpenseDetails(expense.details || '');
    setEditExpenseAmount(expense.amount.toString());
  };

  // Save expense edit
  const saveExpenseEdit = () => {
    if (editingExpense && editExpenseType.trim() && editExpenseDetails.trim() && editExpenseAmount) {
      const amount = parseFloat(editExpenseAmount);
      if (amount > 0) {
        const updatedExpenses = expenses.map(expense =>
          expense.id === editingExpense
            ? {
                ...expense,
                description: editExpenseType.trim(),
                details: editExpenseDetails.trim(),
                amount: amount
              }
            : expense
        );
        onUpdateExpenses(updatedExpenses);
        setEditingExpense(null);
        setEditExpenseType('');
        setEditExpenseDetails('');
        setEditExpenseAmount('');
      }
    }
  };

  // Cancel expense edit
  const cancelExpenseEdit = () => {
    setEditingExpense(null);
    setEditExpenseType('');
    setEditExpenseDetails('');
    setEditExpenseAmount('');
  };

  // Delete expense
  const deleteExpense = (expenseId: string) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      const updatedExpenses = expenses.filter(expense => expense.id !== expenseId);
      onUpdateExpenses(updatedExpenses);
    }
  };

  // Download payment summary only
  const downloadPaymentSummary = () => {
    const activePlayerCount = players.filter(p => p.isActive).length;

    // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const width = 800;
    const headerHeight = 100;
    const rowHeight = 40;
    const tableHeight = (activePlayerCount + 1) * rowHeight; // +1 for header row
    const height = headerHeight + tableHeight + 60;

    canvas.width = width;
    canvas.height = height;

    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    // Header section
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(0, 0, width, headerHeight);

    // Title
    ctx.fillStyle = 'white';
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('💰 Food & Misc Payment Summary', width/2, 35);

    // Summary stats
    ctx.font = '14px Arial';
    ctx.fillText(`Total Expenses: ₹${totalExpenses.toLocaleString()} | Per Head: ₹${perHeadShare.toLocaleString()} | Players: ${activePlayerCount}`, width/2, 70);

    // Table header
    let currentY = headerHeight;
    ctx.fillStyle = '#374151';
    ctx.fillRect(0, currentY, width, rowHeight);

    // Column headers
    ctx.fillStyle = 'white';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Player Name', 130, currentY + 25);
    ctx.fillText('Amount Spent', 290, currentY + 25);
    ctx.fillText('Additional Payments', 470, currentY + 25);
    ctx.fillText('Balance', 650, currentY + 25);

    // Draw vertical lines for columns
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    [200, 360, 540].forEach(x => {
      ctx.beginPath();
      ctx.moveTo(x, currentY);
      ctx.lineTo(x, currentY + tableHeight);
      ctx.stroke();
    });

    currentY += rowHeight;

    // Player rows
    players.filter(p => p.isActive).forEach((player, index) => {
      const isEven = index % 2 === 0;
      const paymentInfo = getPlayerPaymentInfo(player.id);

      // Alternating row background
      ctx.fillStyle = isEven ? '#f9fafb' : 'white';
      ctx.fillRect(0, currentY, width, rowHeight);

      // Row border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, currentY + rowHeight);
      ctx.lineTo(width, currentY + rowHeight);
      ctx.stroke();

      // Player name
      ctx.fillStyle = '#111827';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(player.name, 130, currentY + 25);

      // Amount spent
      ctx.fillStyle = '#374151';
      ctx.font = '12px Arial';
      ctx.fillText(`₹${paymentInfo.spent.toLocaleString()}`, 290, currentY + 25);

      // Additional payments
      const paymentsText = paymentInfo.payments > 0 ? `₹${paymentInfo.payments.toLocaleString()}` : '--';
      ctx.fillText(paymentsText, 470, currentY + 25);

      // Balance with color coding
      ctx.fillStyle = paymentInfo.balance === 0 ? '#6b7280' :
                     paymentInfo.balance < 0 ? '#059669' : '#dc2626';
      ctx.fillText(paymentInfo.status, 650, currentY + 25);

      currentY += rowHeight;
    });

    // Footer
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`Generated on ${new Date().toLocaleDateString()}`, width/2, height - 20);

    // Download the image
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `food_misc_payment_summary.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert('Food & Misc payment summary downloaded! You can now share it on WhatsApp.');
    }, 'image/png');
  };

  // Download food & misc summary with detailed breakdown
  const downloadFoodMiscSummary = () => {
    const activePlayerCount = players.filter(p => p.isActive).length;
    const perHeadShare = activePlayerCount > 0 ? Math.round(totalExpenses / activePlayerCount) : 0;

    // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate required height based on content
    const headerHeight = 120;
    const playerHeaderHeight = 40;
    const playerSummaryHeight = 40;
    const expenseLineHeight = 30;
    const footerHeight = 60;
    const sectionSpacing = 20;

    // Calculate total height needed
    let totalHeight = headerHeight + sectionSpacing;

    // Player summary section
    totalHeight += playerHeaderHeight;
    const playersWithExpenses = Object.keys(expensesByPlayer);
    totalHeight += playersWithExpenses.length * playerSummaryHeight;
    totalHeight += sectionSpacing;

    // Detailed breakdown section
    totalHeight += playerHeaderHeight; // "Detailed Breakdown" header
    Object.values(expensesByPlayer).forEach(({ expenses }) => {
      totalHeight += playerSummaryHeight; // Player name row
      totalHeight += expenses.length * expenseLineHeight; // Expense line items
      totalHeight += sectionSpacing; // Spacing between players
    });

    totalHeight += footerHeight;

    const width = 900;
    canvas.width = width;
    canvas.height = totalHeight;

    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, totalHeight);

    let currentY = 0;

    // Header section
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(0, currentY, width, headerHeight);

    // Title
    ctx.fillStyle = 'white';
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('🍽️ Food & Misc Expenses Summary', width/2, currentY + 40);

    // Summary stats
    ctx.font = '16px Arial';
    ctx.fillText(`Total Expenses: ₹${totalExpenses.toLocaleString()} | Per Head: ₹${perHeadShare.toLocaleString()} | Active Players: ${activePlayerCount}`, width/2, currentY + 70);

    // Date
    ctx.font = '12px Arial';
    ctx.fillText(`Generated on ${new Date().toLocaleDateString()}`, width/2, currentY + 100);

    currentY += headerHeight + sectionSpacing;

    // Player Summary Section
    ctx.fillStyle = '#374151';
    ctx.fillRect(0, currentY, width, playerHeaderHeight);

    ctx.fillStyle = 'white';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Player Payment Summary', width/2, currentY + 28);

    currentY += playerHeaderHeight;

    // Player summary table headers
    ctx.fillStyle = '#e5e7eb';
    ctx.fillRect(0, currentY, width, playerSummaryHeight);

    ctx.fillStyle = '#374151';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Player Name', 150, currentY + 25);
    ctx.fillText('Total Paid', 350, currentY + 25);
    ctx.fillText('Share (₹' + perHeadShare.toLocaleString() + ')', 550, currentY + 25);
    ctx.fillText('Balance', 750, currentY + 25);

    // Draw column borders
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1;
    [250, 450, 650].forEach(x => {
      ctx.beginPath();
      ctx.moveTo(x, currentY);
      ctx.lineTo(x, currentY + playerSummaryHeight);
      ctx.stroke();
    });

    currentY += playerSummaryHeight;

    // Player summary rows
    Object.values(expensesByPlayer).forEach(({ player, totalPaid }, index) => {
      const balance = perHeadShare - totalPaid;
      const isEven = index % 2 === 0;

      // Alternating row background
      ctx.fillStyle = isEven ? '#f9fafb' : 'white';
      ctx.fillRect(0, currentY, width, playerSummaryHeight);

      // Row border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, currentY + playerSummaryHeight);
      ctx.lineTo(width, currentY + playerSummaryHeight);
      ctx.stroke();

      // Column borders
      ctx.strokeStyle = '#e5e7eb';
      [250, 450, 650].forEach(x => {
        ctx.beginPath();
        ctx.moveTo(x, currentY);
        ctx.lineTo(x, currentY + playerSummaryHeight);
        ctx.stroke();
      });

      // Player data
      ctx.fillStyle = '#111827';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(player.name, 150, currentY + 25);
      ctx.fillText(`₹${totalPaid.toLocaleString()}`, 350, currentY + 25);
      ctx.fillText(`₹${perHeadShare.toLocaleString()}`, 550, currentY + 25);

      // Balance with color coding
      const balanceText = balance < 0 ? `Owed: ₹${Math.abs(balance).toLocaleString()}` :
                         balance > 0 ? `Owes: ₹${balance.toLocaleString()}` : 'Even';
      ctx.fillStyle = balance < 0 ? '#059669' : balance > 0 ? '#dc2626' : '#6b7280';
      ctx.fillText(balanceText, 750, currentY + 25);

      currentY += playerSummaryHeight;
    });

    currentY += sectionSpacing;

    // Detailed Breakdown Section
    ctx.fillStyle = '#374151';
    ctx.fillRect(0, currentY, width, playerHeaderHeight);

    ctx.fillStyle = 'white';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Detailed Expense Breakdown', width/2, currentY + 28);

    currentY += playerHeaderHeight;

    // Detailed breakdown for each player
    Object.values(expensesByPlayer).forEach(({ player, expenses, totalPaid }) => {
      // Player section header
      ctx.fillStyle = '#6b7280';
      ctx.fillRect(0, currentY, width, playerSummaryHeight);

      ctx.fillStyle = 'white';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`${player.name} - Total: ₹${totalPaid.toLocaleString()}`, 20, currentY + 25);

      currentY += playerSummaryHeight;

      // Expense line items
      expenses.forEach((expense, index) => {
        const isEven = index % 2 === 0;

        // Alternating row background
        ctx.fillStyle = isEven ? '#f3f4f6' : 'white';
        ctx.fillRect(0, currentY, width, expenseLineHeight);

        // Row border
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, currentY + expenseLineHeight);
        ctx.lineTo(width, currentY + expenseLineHeight);
        ctx.stroke();

        // Expense details
        ctx.fillStyle = '#374151';
        ctx.font = '12px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`• ${expense.description}`, 40, currentY + 20);
        ctx.fillText(expense.details || '-', 300, currentY + 20);

        ctx.textAlign = 'right';
        ctx.fillText(`₹${expense.amount.toLocaleString()}`, width - 40, currentY + 20);

        currentY += expenseLineHeight;
      });

      currentY += sectionSpacing;
    });

    // Footer
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`Food & Misc Expenses - Cricket Trip Dehradun`, width/2, totalHeight - 20);

    // Download the image
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `food_misc_expenses_detailed.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert('Food & Misc detailed summary downloaded! You can now share it on WhatsApp.');
    }, 'image/png');
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
      {/* Main Accordion Header */}
      <div
        className="accordion-header px-6 py-4 cursor-pointer flex justify-between items-center transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-4">
          <span className="text-lg font-semibold">Food & Misc</span>
          {!isOpen && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-sm text-gray-300">
                Total: ₹{totalExpenses.toLocaleString()}
              </span>
              <span className="text-gray-300">|</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFoodMiscSummary();
                }}
                className="text-sm text-gray-300 hover:text-blue-200 transition-colors cursor-pointer"
              >
                Download Expense Summary
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadPaymentSummary();
                }}
                className="text-sm text-gray-300 hover:text-blue-200 transition-colors cursor-pointer"
              >
                Download Payment Summary
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <svg
            className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Main Accordion Content */}
      {isOpen && (
        <div>
          {/* Player Payment Summary Table */}
          <table className="w-full border-collapse">
            <thead>
              <tr className="table-header">
                <th className="px-4 py-3 text-left font-medium">Player Name</th>
                <th className="px-4 py-3 text-center font-medium">Total Paid Amount</th>
                <th className="px-4 py-3 text-center font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(expensesByPlayer).map(({ player, totalPaid }) => (
                <React.Fragment key={player.id}>
                  {/* Main Player Row */}
                  <tr
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => togglePlayerAccordion(player.id)}
                  >
                    <td className="px-4 py-2 text-gray-900">{player.name}</td>
                    <td className="px-4 py-2 text-center text-sm">
                      ₹{totalPaid.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-center text-sm">
                      <svg
                        className={`w-4 h-4 transform transition-transform mx-auto ${
                          expandedPlayers.has(player.id) ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </td>
                  </tr>

                  {/* Nested Accordion - Expense Details */}
                  {expandedPlayers.has(player.id) && (
                    <tr>
                      <td colSpan={3} className="px-4 py-4">
                        <div className="bg-gray-100 border-2 border-gray-300 rounded-md shadow-sm">
                          <table className="w-full border-collapse border border-gray-400">
                            <thead>
                              <tr className="bg-gray-300 border-b-2 border-gray-400">
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-800 border-r border-gray-400">Expense Type</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-800 border-r border-gray-400">Details</th>
                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-800 border-r border-gray-400">Amount</th>
                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-800">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {expensesByPlayer[player.id].expenses.map((expense, index) => (
                                <tr key={expense.id} className="border-b border-gray-300 hover:bg-gray-50">
                                  {editingExpense === expense.id ? (
                                    <>
                                      <td className="px-4 py-3 text-sm border-r border-gray-300">
                                        <input
                                          type="text"
                                          value={editExpenseType}
                                          onChange={(e) => setEditExpenseType(e.target.value)}
                                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                          autoFocus
                                        />
                                      </td>
                                      <td className="px-4 py-3 text-sm border-r border-gray-300">
                                        <input
                                          type="text"
                                          value={editExpenseDetails}
                                          onChange={(e) => setEditExpenseDetails(e.target.value)}
                                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                        />
                                      </td>
                                      <td className="px-4 py-3 text-center text-sm border-r border-gray-300">
                                        <input
                                          type="number"
                                          value={editExpenseAmount}
                                          onChange={(e) => setEditExpenseAmount(e.target.value)}
                                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                        />
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <div className="flex gap-1 justify-center">
                                          <button
                                            onClick={saveExpenseEdit}
                                            className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                          >
                                            Save
                                          </button>
                                          <button
                                            onClick={cancelExpenseEdit}
                                            className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-4 py-3 text-sm border-r border-gray-300">{expense.description}</td>
                                      <td className="px-4 py-3 text-sm border-r border-gray-300">{expense.details}</td>
                                      <td className="px-4 py-3 text-center text-sm font-medium border-r border-gray-300">₹{expense.amount.toLocaleString()}</td>
                                      <td className="px-4 py-3 text-center">
                                        <div className="flex gap-1 justify-center">
                                          <button
                                            onClick={() => startEditExpense(expense)}
                                            className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() => deleteExpense(expense.id)}
                                            className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Add New Expense Form */}
          <div className="border-t bg-gray-50 p-4">
            <h3 className="text-lg font-medium mb-4 text-gray-800">Add New Expense</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Expense Type</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Details</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Amount</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Paid By</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={newExpenseType}
                        onChange={(e) => setNewExpenseType(e.target.value)}
                        placeholder="e.g., Food, Snacks, Drinks"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={newExpenseDetails}
                        onChange={(e) => setNewExpenseDetails(e.target.value)}
                        placeholder="Enter details..."
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={newExpenseAmount}
                        onChange={(e) => setNewExpenseAmount(e.target.value)}
                        placeholder="0"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={newExpensePaidBy}
                        onChange={(e) => setNewExpensePaidBy(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="">Select Player</option>
                        {players.filter(p => p.isActive).map(player => (
                          <option key={player.id} value={player.id}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={addExpense}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                        disabled={!newExpenseType.trim() || !newExpenseDetails.trim() || !newExpenseAmount || !newExpensePaidBy}
                      >
                        Add
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Player Payment Summary Section */}
          <div className="border-t bg-gray-50">
            <div
              className="px-4 py-3 cursor-pointer flex justify-between items-center hover:bg-gray-100 transition-colors"
              onClick={() => setIsPaymentSummaryOpen(!isPaymentSummaryOpen)}
            >
              <h3 className="text-lg font-medium text-gray-800">Player Payment Summary</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Per Head Share: ₹{perHeadShare.toLocaleString()}
                </span>
                <svg
                  className={`w-4 h-4 transform transition-transform ${isPaymentSummaryOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {isPaymentSummaryOpen && (
              <div className="px-4 pb-4">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-3 text-left font-medium border border-gray-300">Player Name</th>
                      <th className="px-4 py-3 text-center font-medium border border-gray-300">Amount Spent</th>
                      <th className="px-4 py-3 text-center font-medium border border-gray-300">Additional Payments</th>
                      <th className="px-4 py-3 text-center font-medium border border-gray-300">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.filter(p => p.isActive).map((player) => {
                      const paymentInfo = getPlayerPaymentInfo(player.id);
                      return (
                        <tr key={player.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-900 border border-gray-300">{player.name}</td>
                          <td className="px-4 py-2 text-center text-sm border border-gray-300">
                            ₹{paymentInfo.spent.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-center text-sm border border-gray-300">
                            {editingPaymentColumn === player.id ? (
                              <div className="flex items-center justify-center gap-2">
                                <input
                                  type="number"
                                  value={editPaymentAmount}
                                  onChange={(e) => setEditPaymentAmount(e.target.value)}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                                  autoFocus
                                  onKeyPress={(e) => e.key === 'Enter' && savePaymentEdit()}
                                />
                                <button
                                  onClick={savePaymentEdit}
                                  className="text-green-600 hover:text-green-800"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={cancelPaymentEdit}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <span
                                className="cursor-pointer text-blue-600 hover:text-blue-800"
                                onDoubleClick={() => startEditPayment(player.id)}
                              >
                                {paymentInfo.payments > 0 ? `₹${paymentInfo.payments.toLocaleString()}` : '--'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center text-sm border border-gray-300">
                            <span className={`px-2 py-1 rounded text-xs ${
                              paymentInfo.balance === 0 ? 'text-gray-500' :
                              paymentInfo.balance < 0 ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {paymentInfo.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};